import Phaser from "phaser";
import { ObjectPool } from "@yjsgames/object-pool";
import type { RectMask } from "@yjsgames/phaser-ui";
import type { GhostEmitter, ProjectileDespawn } from "../objects/Projectile.js";
import { Projectile } from "../objects/Projectile.js";
import { Brick } from "../objects/Brick.js";
import {
  createFxImagePool,
  createFxRectPool,
  createFxTextPool,
  type FxImagePool,
  type FxRectPool,
  type FxTextPool,
} from "../objects/fxImagePool.js";
import { BattlefieldFxBudget } from "./battlefieldFxBudget.js";
import { FORCE_LOW_SPEC_RENDER_ENABLED } from "../env/appFlavor.js";

export const MAX_ACTIVE_PROJECTILES = 16;
const REGEN_SCAN_INTERVAL_MS = 200;

export interface BattlefieldRuntimePoolsStats {
  readonly created: number;
  readonly reused: number;
  readonly idle: number;
}

export class BattlefieldRuntimePools {
  readonly bricks: Phaser.GameObjects.Group;
  readonly projectiles: Phaser.GameObjects.Group;
  readonly projectilePool: ObjectPool<Projectile>;
  readonly brickPool: ObjectPool<Brick>;
  readonly fxImagePool: FxImagePool;
  readonly fxRectPool: FxRectPool;
  readonly fxTextPool: FxTextPool;
  private readonly fxBudget = new BattlefieldFxBudget();
  private nextRegenScanAt = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly gameviewMask: () => RectMask | undefined,
    private readonly onBrickPointer: (brick: Brick) => void,
  ) {
    this.bricks = scene.add.group();
    this.projectiles = scene.add.group();
    this.projectilePool = new ObjectPool<Projectile>({
      create: () => {
        const p = new Projectile(scene);
        this.projectiles.add(p);
        return p;
      },
      onRelease: (p) => p.deactivate(),
    });
    this.brickPool = new ObjectPool<Brick>({
      create: () => {
        const b = new Brick(scene);
        this.bricks.add(b);
        b.setPointerHandler(() => this.onBrickPointer(b));
        return b;
      },
      onRelease: (b) => b.deactivate(),
    });
    this.fxImagePool = createFxImagePool(scene, "projectile_basic_white");
    this.fxRectPool = createFxRectPool(scene);
    this.fxTextPool = createFxTextPool(scene);
  }

  attachCollision(handler: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback): void {
    this.scene.physics.add.overlap(this.projectiles, this.bricks, handler);
  }

  readonly emitGhost: GhostEmitter = () => {};

  allowImpactFx(): boolean {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) return false;
    return this.fxBudget.allow(this.scene.time.now, "impactFx");
  }

  allowDestroyFx(): boolean {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) return false;
    return this.fxBudget.allow(this.scene.time.now, "destroyFx");
  }

  allowTextFx(): boolean {
    return false;
  }

  allowMuzzleFx(): boolean {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) return false;
    return this.fxBudget.allow(this.scene.time.now, "muzzleFx");
  }

  remainingProjectileSlots(): number {
    return Math.max(0, MAX_ACTIVE_PROJECTILES - this.countActiveProjectiles());
  }

  shouldScanRegen(nowMs: number): boolean {
    if (nowMs < this.nextRegenScanAt) return false;
    this.nextRegenScanAt = nowMs + REGEN_SCAN_INTERVAL_MS;
    return true;
  }

  readonly releaseProjectile: ProjectileDespawn = (proj) => {
    this.projectilePool.release(proj);
  };

  maskFx(go: Phaser.GameObjects.GameObject | readonly Phaser.GameObjects.GameObject[]): void {
    const mask = this.gameviewMask();
    if (!mask) return;
    if (Array.isArray(go)) {
      for (const g of go) mask.apply(g);
      return;
    }
    mask.apply(go as Phaser.GameObjects.GameObject);
  }

  clear(): void {
    this.nextRegenScanAt = 0;
    for (const obj of this.bricks.getChildren()) this.brickPool.release(obj as Brick);
    for (const obj of this.projectiles.getChildren()) this.releaseProjectile(obj as Projectile);
  }

  countActiveBricks(): number {
    return this.bricks.countActive(true);
  }

  countActiveProjectiles(): number {
    return this.projectiles.countActive(true);
  }

  projectileStats(): BattlefieldRuntimePoolsStats {
    return {
      created: this.projectilePool.createdCount,
      reused: this.projectilePool.reusedCount,
      idle: this.projectilePool.idleCount,
    };
  }
}
